import React, { useMemo, useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Tabs, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { useAuth } from '@frontend/context/authContext';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { graphql, useFragment } from '@frontend/gql/social';
import { useRouteMatch, useHistory, useLocation, useParams } from 'react-router-dom';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { ClientFeature } from '@frontend/app/constants';

import { useGetCurrentClient } from '@frontend/app/hooks/client/useGetCurrentClient';
import { Switch } from '@frontend/shadcn/components/ui/switch';
import { Label } from '@frontend/shadcn/components/ui/label';
import { DateType } from '../Analytics/types';
import { DateRangeForm } from '../Analytics/components/date-range-form';
import {
  useAdsAnalyticsParams,
  useAdsDateDefaults,
  useIntervalRanges,
  useAdsQueryData,
  useUrlInitialization,
  useAdsFilterFormData,
  useAdsDefaultValues,
  useSocialAdsOverviewTab,
  useSocialAdsTopAdsData,
  useSocialAdsSocialAccountsTab,
  useSocialAdsSocialAccountsData,
  useSocialAdsTopAdsTab,
  type AdsQueryVariables,
} from './hooks';
import {
  Header,
  HeaderLayout,
  HeaderMainContent,
  HeaderH1,
  HeaderActions,
  HeaderTabs,
  HeaderTabsList,
  HeaderTabsTrigger,
} from '../components/header';
import { FilterSheet, RefreshButton, FilterButton } from '../components/shared';
import { useFilterSheet } from '../hooks/shared';
import { getClientCreatedDateForDateRange } from '../utils/shared';
import { AdsFiltersForm, AdsFiltersFormData } from './components/ads-filters-form';
import { useSearchMembers, useMembersByIds, useMergeMembersData } from '../Analytics/hooks';
import SocialAdsOverview from './social-ads-overview';
import SocialAdsTopAds from './social-ads-top-ads';
import SocialAdsSocialAccounts from './social-ads-social-accounts';
import {
  defaultAdsColumns,
  getDefaultVisibleAdsColumnIds,
  AdsColumnDefinitionInput,
} from './components/ads-table/ads-table-column-config';
import type { SocialAccountsColumn } from './components/social-accounts-table/social-accounts-column-dialog';
import { SocialAdsFilterBadges } from './components/filter-badges';
import {
  useAdAccountBadges,
  useAdCampaignBadges,
  useAdObjectiveBadges,
  useMemberBadges,
  usePartnershipAdsBadge,
} from './components/filter-badges/hooks';
// REMOVED: getDefaultSelectedCampaignsAndSets - now handled by useAdsDefaultValues hook

const DEFAULT_VARIABLES: Omit<AdsQueryVariables, 'clientId'> = {
  dateType: 'last6Months' as DateType,
  startDate: '',
  endDate: '',
  showNonAttributedData: false,
  fromOldData: false,
  // Note: Filter parameters (adAccountIds, etc.) are not yet supported by the backend GraphQL schema
  // These will be added back once the backend MetaInsightsFilterInput type is updated
  adAccountIds: undefined, // Will be set by useUrlInitialization
  adCampaignAndSetIds: undefined, // Will be set by useUrlInitialization
  adObjectiveIds: [],
  memberIds: [],
  compareWithPartnershipAds: false,
};

const DEFAULT_CURRENCY = 'USD';

// Fragment to access currency from currentInterval
const currencyFragment = graphql(`
  fragment SocialAds_CurrencyFragment on MetaSumInsights {
    totalSpend {
      currency
    }
  }
`);

/*
  Component
*/
export const SocialAds = () => {
  const { clientInfo } = useAuth();
  const { client } = useGetCurrentClient();
  const clientCreatedDate = client?.createdDate;
  const clientId = clientInfo?.id;

  // Feature flag for social ads filters
  const isSocialAdsFiltersEnabled = useClientFeatureEnabled(ClientFeature.SOCIAL_ADS_FILTERS);

  // Routing logic for URL-based tab navigation
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { tab } = useParams<{ tab: string }>();

  // Determine current tab based on URL parameter
  const getCurrentTab = () => {
    switch (tab) {
      case 'social-accounts':
        return 'social-accounts';
      case 'overview':
        return 'overview';
      case 'top-ads':
        return 'top-ads';
      default:
        // Default to 'overview' for any unrecognized tab
        return 'overview';
    }
  };

  const currentTab = getCurrentTab();

  // Column ordering state for Top Ads table
  const [userSelectedAdsColumns, setUserSelectedAdsColumns] = useState<AdsColumnDefinitionInput[]>([]);

  // Column ordering state for Social Accounts table
  const [userSelectedSocialAccountsColumns, setUserSelectedSocialAccountsColumns] = useState<SocialAccountsColumn[]>(
    [],
  );

  // Determine final ads columns: prioritize user choice, fall back to defaults
  const selectedAdsColumns = useMemo(() => {
    // If user has made no selections, use defaults
    if (userSelectedAdsColumns.length === 0) {
      const defaultColumnIds = getDefaultVisibleAdsColumnIds();
      return defaultAdsColumns.map((col) => ({
        ...col,
        hidden: !defaultColumnIds.includes(col.id),
      }));
    }

    // User has made selections - respect their choices completely
    return userSelectedAdsColumns;
  }, [userSelectedAdsColumns]);

  // Handle tab changes
  const handleTabChange = (value: string) => {
    // Preserve query string parameters when navigating between tabs, but remove page parameter
    const searchParams = new URLSearchParams(location.search);

    // Remove page parameter when changing tabs to reset pagination
    searchParams.delete('page');

    // Use the route pattern (path) instead of the matched URL (url) to construct the new pathname
    // path contains the route pattern like '/social/ads/:tab'
    // url contains the actual matched URL like '/social/ads/overview'
    const newPathname = path.replace(':tab', value);

    history.push({
      pathname: newPathname,
      search: searchParams.toString(),
    });
  };

  // Use custom hooks to organize logic
  const { defaultVariablesWithDates } = useAdsDateDefaults({
    clientCreatedDate,
    defaultVariables: DEFAULT_VARIABLES,
  });

  const {
    queryVariables,
    handleDateRangeSubmit,
    handleShowNonAttributedDataChange,
    handleFromOldDataChange,
    updateUrlParams,
    handleApplyFilters,
  } = useAdsAnalyticsParams({
    clientId,
    defaultVariables: defaultVariablesWithDates,
    clientCreatedDate,
  });

  const { currentIntervalRange, previousIntervalRange } = useIntervalRanges({
    queryVariables,
  });

  const { fetching, refetching, processedData, handleRefetch } = useAdsQueryData({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate: queryVariables.startDate,
    endDate: queryVariables.endDate,
    fromOldData: queryVariables.fromOldData,
    // Pass filter parameters to enable refetch detection (even though GraphQL query doesn't use them yet)
    // This allows the hook to detect changes and trigger refetch when filters are applied
    adAccountIds: queryVariables.adAccountIds,
    adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
    adObjectiveIds: queryVariables.adObjectiveIds,
    memberIds: queryVariables.memberIds,
    compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
    tab: currentTab,
  });

  // Placeholder: Top Ads hook usage (for future extension)
  const {
    fetching: topAdsFetching,
    refetching: topAdsRefetching,
    processedData: topAdsProcessedData,
    handleRefetch: handleTopAdsRefetch,
  } = useSocialAdsTopAdsData({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate: queryVariables.startDate,
    endDate: queryVariables.endDate,
    fromOldData: queryVariables.fromOldData,
    adAccountIds: queryVariables.adAccountIds,
    adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
    adObjectiveIds: queryVariables.adObjectiveIds,
    memberIds: queryVariables.memberIds,
    compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
    tab: currentTab,
  });

  // Placeholder: Social Accounts hook usage (for future extension)
  const {
    fetching: socialAccountsFetching,
    refetching: socialAccountsRefetching,
    processedData: socialAccountsProcessedData,
    handleRefetch: handleSocialAccountsRefetch,
  } = useSocialAdsSocialAccountsData({
    clientId,
    currentIntervalRange,
    previousIntervalRange,
    startDate: queryVariables.startDate,
    endDate: queryVariables.endDate,
    fromOldData: queryVariables.fromOldData,
    adAccountIds: queryVariables.adAccountIds,
    adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
    adObjectiveIds: queryVariables.adObjectiveIds,
    memberIds: queryVariables.memberIds,
    compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
    tab: currentTab,
  });

  // Destructure processed data for cleaner access
  const {
    metaAdAccounts,
    currentInterval,
    currentIntervalsIntervalInsights,
    currentIntervalsIntervalInsightsNotAspireSourced,
    previousInterval,
    currentIntervalNotAspireSourced,
    previousIntervalNotAspireSourced,
    topAds,
    topSocialAccounts,
  } = processedData;

  // Function to get loading states specific to the current tab
  const getTabSpecificLoadingStates = () => {
    switch (currentTab) {
      case 'overview':
        return {
          isLoading: fetching || refetching,
          isRefreshing: refetching,
        };
      case 'top-ads':
        return {
          isLoading: topAdsFetching || topAdsRefetching,
          isRefreshing: topAdsRefetching,
        };
      case 'social-accounts':
        return {
          isLoading: socialAccountsFetching || socialAccountsRefetching,
          isRefreshing: socialAccountsRefetching,
        };
      default:
        return {
          isLoading: fetching || refetching,
          isRefreshing: refetching,
        };
    }
  };

  // Get tab-specific loading states
  const { isLoading, isRefreshing } = getTabSpecificLoadingStates();

  // Extract currency from API data, fallback to USD if not available
  const currentIntervalCurrencyData = useFragment(currencyFragment, currentInterval);
  const currency = currentIntervalCurrencyData?.totalSpend?.currency || DEFAULT_CURRENCY;

  // Use the new hook to process data and get props for SocialAdsOverview
  const { socialAdsOverviewTabProps } = useSocialAdsOverviewTab({
    processedData: {
      currentInterval,
      previousInterval,
      currentIntervalNotAspireSourced,
      previousIntervalNotAspireSourced,
      currentIntervalsIntervalInsights,
      currentIntervalsIntervalInsightsNotAspireSourced,
      currentIntervalRange,
      topAds,
      topSocialAccounts,
      currency,
      metaAdAccounts,
    },
    queryVariables,
    isLoading,
    onShowNonAttributedDataChange: handleShowNonAttributedDataChange,
    onFromOldDataChange: handleFromOldDataChange,
    handleTabChange,
  });

  const socialAdsTopAdsTabProps = useSocialAdsTopAdsTab({
    isLoading,
    isRefreshing,
    data: topAdsProcessedData,
    selectedColumns: selectedAdsColumns,
    setSelectedColumns: setUserSelectedAdsColumns,
  });
  const socialAdsSocialAccountsTabProps = useSocialAdsSocialAccountsTab({
    isLoading,
    isRefreshing,
    data: socialAccountsProcessedData,
    selectedColumns: userSelectedSocialAccountsColumns,
    setSelectedColumns: setUserSelectedSocialAccountsColumns,
  });

  // Ads filter form data
  const { adAccounts, adCampaigns, adObjectives, isFetchingAdAccounts, isFetchingAdsData } =
    useAdsFilterFormData(clientId);

  // Use centralized default values
  const defaultValues = useAdsDefaultValues({
    queryVariables,
    adAccounts,
    adCampaigns,
  });

  // Initialize URL params with default ad accounts (after adAccounts is available)
  useUrlInitialization({
    clientId,
    clientCreatedDate,
    defaultDateType: DEFAULT_VARIABLES.dateType,
    defaultShowNonAttributedData: DEFAULT_VARIABLES.showNonAttributedData,
    defaultFromOldData: DEFAULT_VARIABLES.fromOldData,
    defaultAdAccountIds: defaultValues.adAccountIds,
    defaultAdCampaignAndSetIds: defaultValues.adCampaignAndSetIds,
    isFetchingAdAccounts,
    updateUrlParams,
  });

  // Member search functionality
  const [searchMemberTerm, setMemberSearchTerm] = React.useState<string>('');

  // Query for search term
  const { data: searchData, loading: searchLoading, error: searchError } = useSearchMembers(searchMemberTerm);

  // Use memberIds from URL params for the member data
  const memberIdsFromQuery = useMemo(
    () => (queryVariables?.memberIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id)),
    [queryVariables?.memberIds],
  );
  const { data: memberIdsData, loading: memberIdsLoading, error: memberIdsError } = useMembersByIds(memberIdsFromQuery);

  // Combine and deduplicate the results
  const {
    data: { members = [] },
    loading: isFetchingMembers,
  } = useMergeMembersData(searchData, searchLoading, searchError, memberIdsData, memberIdsLoading, memberIdsError);

  // Convert members to match the updated Member type with string id
  const mappedMembers = useMemo(
    () =>
      members.map((member) => ({
        ...member,
        id: String(member.id),
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        profilePicture: member.profilePicture ?? undefined,
      })),
    [members],
  );

  // Filter sheet state management
  const {
    isOpen: isFiltersOpen,
    handleOpen: handleOpenFilters,
    handleCancel: handleCancelFilters,
  } = useFilterSheet({
    onApplyFilters: () => {}, // Will be overridden by handleFormSubmit
  });

  // Filter functionality with form support - SIMPLIFIED
  const handleFormSubmit = useCallback(
    (formData: AdsFiltersFormData) => {
      handleApplyFilters(formData); // ✅ Direct pass-through
      handleCancelFilters();
    },
    [handleApplyFilters, handleCancelFilters],
  );

  // Filter badge hooks
  const { adAccountBadgesProps } = useAdAccountBadges({
    adAccounts,
    adAccountIds: queryVariables.adAccountIds,
  });

  const { adCampaignBadgesProps } = useAdCampaignBadges({
    adCampaigns,
    adCampaignAndSetIds: queryVariables.adCampaignAndSetIds,
  });

  const { adObjectiveBadgesProps } = useAdObjectiveBadges({
    adObjectives,
    adObjectiveIds: queryVariables.adObjectiveIds,
  });

  const { memberBadgesProps } = useMemberBadges({
    memberIds: queryVariables.memberIds,
    members: mappedMembers,
  });

  const { partnershipAdsBadgeProps } = usePartnershipAdsBadge({
    compareWithPartnershipAds: queryVariables.compareWithPartnershipAds,
  });

  // Handle filter removal
  const handleRemoveFilters = useCallback(
    (type: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      // Create a new form data object with the specific filter removed
      const newFormData = {
        adAccountIds: type === 'adAccountIds' ? [] : queryVariables.adAccountIds || [],
        adCampaignAndSetIds: type === 'adCampaignAndSetIds' ? [] : queryVariables.adCampaignAndSetIds || [],
        adObjectiveIds: type === 'adObjectiveIds' ? [] : queryVariables.adObjectiveIds || [],
        memberIds: type === 'memberIds' ? [] : queryVariables.memberIds || [],
        compareWithPartnershipAds:
          type === 'compareWithPartnershipAds' ? false : queryVariables.compareWithPartnershipAds,
        showNonAttributedData: type === 'showNonAttributedData' ? false : queryVariables.showNonAttributedData,
      };

      // Apply the updated filters
      handleApplyFilters(newFormData);
    },
    [queryVariables, handleApplyFilters],
  );

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    // Create a new form data object with all filters cleared to default values
    const newFormData = {
      adAccountIds: [],
      adCampaignAndSetIds: [],
      adObjectiveIds: [],
      memberIds: [],
      compareWithPartnershipAds: false,
      showNonAttributedData: false,
      // Note: dateType and fromOldData are handled separately
      // by the date range form and toggle switches, so we don't reset them here
    };

    // Apply the cleared filters
    handleApplyFilters(newFormData);
  }, [handleApplyFilters]);

  const handleDataRefetch = useCallback(() => {
    switch (currentTab) {
      case 'overview':
        handleRefetch();
        break;
      case 'top-ads':
        handleTopAdsRefetch();
        break;
      case 'social-accounts':
        handleSocialAccountsRefetch();
        break;
    }
  }, [currentTab, handleRefetch, handleTopAdsRefetch, handleSocialAccountsRefetch]);

  return (
    <div className="flex h-full flex-col">
      <TooltipProvider>
        <Helmet>
          <title>Ads Dashboard - Aspire</title>
        </Helmet>
        <Tabs value={currentTab} onValueChange={handleTabChange}>
          <Header>
            <HeaderLayout>
              <HeaderMainContent>
                <HeaderH1>Ads Dashboard</HeaderH1>
              </HeaderMainContent>
              <HeaderActions>
                <RefreshButton
                  isRefreshing={isRefreshing}
                  onClick={handleDataRefetch}
                  disabled={false} // Always enabled since we have multiple tabs
                  dataAttribute="refresh-ads-data"
                  testId="refresh-ads-button"
                  className="hidden"
                />
                <DateRangeForm
                  onApply={handleDateRangeSubmit}
                  defaultValues={{
                    dateType: queryVariables.dateType,
                    dateRange:
                      queryVariables.startDate && queryVariables.endDate
                        ? {
                            from: queryVariables.startDate,
                            to: queryVariables.endDate,
                          }
                        : undefined,
                  }}
                  minDate={getClientCreatedDateForDateRange(clientCreatedDate)}
                  aria-label="Date range selector"
                />
                {isSocialAdsFiltersEnabled && (
                  <FilterButton
                    onClick={handleOpenFilters}
                    isExpanded={isFiltersOpen}
                    dataAttribute="open-ads-filters"
                  />
                )}
              </HeaderActions>
            </HeaderLayout>
            <HeaderTabs>
              <HeaderTabsList>
                <HeaderTabsTrigger value="overview" data-dd-action-name="social-ads-overview-tab">
                  Overview
                </HeaderTabsTrigger>
                <HeaderTabsTrigger value="top-ads" data-dd-action-name="social-ads-top-ads-tab">
                  Ads
                </HeaderTabsTrigger>
                <HeaderTabsTrigger value="social-accounts" data-dd-action-name="social-ads-social-accounts-tab">
                  Members
                </HeaderTabsTrigger>
              </HeaderTabsList>
            </HeaderTabs>
          </Header>

          {/* Filter Badges and Toggles Row */}
          <div className="mt-4">
            <div className="px-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between w-full">
                {/* Left: Filter Badges */}
                <div className="inline-flex items-center flex-wrap">
                  {isSocialAdsFiltersEnabled && (
                    <SocialAdsFilterBadges
                      adAccountBadgesProps={adAccountBadgesProps}
                      adCampaignBadgesProps={adCampaignBadgesProps}
                      adObjectiveBadgesProps={adObjectiveBadgesProps}
                      memberBadgesProps={memberBadgesProps}
                      partnershipAdsBadgeProps={partnershipAdsBadgeProps}
                      onOpenFilters={handleOpenFilters}
                      onRemoveFilters={handleRemoveFilters}
                      onClearAllFilters={handleClearAllFilters}
                    />
                  )}
                </div>
                {/* Right: Toggles (only show on overview tab) */}
                {currentTab === 'overview' && (
                  <div className="flex flex-col gap-2 sm:flex-row sm:gap-4 items-end sm:items-center mt-2 sm:mt-0">
                    <div className="flex items-center gap-2 hidden">
                      <Switch
                        id="from-old-data"
                        checked={queryVariables.fromOldData}
                        onCheckedChange={handleFromOldDataChange}
                        className="data-[state=checked]:bg-primary"
                        disabled={isLoading}
                        data-dd-action-name="social-ads-from-old-data-toggle"
                      />
                      <Label
                        htmlFor="from-old-data"
                        className="text-sm font-normal text-[#16282d] cursor-pointer whitespace-nowrap"
                      >
                        Use old data source
                      </Label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-4 pb-6">
            <TabsContent value="overview">
              <SocialAdsOverview {...socialAdsOverviewTabProps} />
            </TabsContent>
            <TabsContent value="top-ads">
              <SocialAdsTopAds {...socialAdsTopAdsTabProps} />
            </TabsContent>
            <TabsContent value="social-accounts">
              <SocialAdsSocialAccounts {...socialAdsSocialAccountsTabProps} />
            </TabsContent>
          </div>
        </Tabs>

        {/* Filter Sheet using shared component */}
        {isSocialAdsFiltersEnabled && (
          <FilterSheet
            isOpen={isFiltersOpen}
            onOpenChange={(open) => {
              if (open) {
                handleOpenFilters();
              } else {
                handleCancelFilters();
              }
            }}
            title="Ads Filters"
            onCancel={handleCancelFilters}
            onApply={() => {}} // No-op since form handles its own submission
            cancelDataAttribute="cancel-ads-filters"
            applyDataAttribute="apply-ads-filters"
          >
            <AdsFiltersForm
              adAccounts={adAccounts}
              adCampaigns={adCampaigns}
              adObjectives={adObjectives}
              isFetchingAdAccounts={isFetchingAdAccounts}
              isFetchingAdsData={isFetchingAdsData}
              members={mappedMembers}
              setMemberSearchTerm={setMemberSearchTerm}
              isFetchingMembers={isFetchingMembers ?? false}
              defaultValues={defaultValues}
              onSubmit={handleFormSubmit}
              onCancel={handleCancelFilters}
            />
          </FilterSheet>
        )}
      </TooltipProvider>
    </div>
  );
};
